import { useEffect, useState } from 'react';
import { STAGES, type DecisionView, type MessageView, type Review, type StatusStepId, type UpdateOnboardingRequest } from '@upm/domain';
import { api, ApiError } from '../lib/api';
import { fmtDate, fmtDateTime, fmtCalendarDate } from '../lib/format';
import { Banner, Button, Pill, TableBox, Td, TextArea, TextField, Th, cx } from '../ui';

type Patch = (b: UpdateOnboardingRequest, done?: string) => Promise<void>;
const today = () => new Date().toISOString().slice(0, 10);

export function StatusControlsTab({ r, patch }: { r: Review; patch: Patch }) {
  const [freeze, setFreeze] = useState(r.scope.freezeDate?.slice(0, 10) ?? '');
  useEffect(() => { setFreeze(r.scope.freezeDate?.slice(0, 10) ?? ''); }, [r.scope.freezeDate]);
  const setStep = (id: StatusStepId, done: boolean) => void patch({ steps: { [id]: done ? 'done' : 'todo' } });
  return (
    <div className="flex flex-col gap-6">
      <p className="m-0 text-muted">What the firm sees on its migration status page. Plan steps follow the onboarding automatically. Sign the agreement ticks itself when the agreement is signed in the app; tick it here only for one signed outside the app, along with the Prepare, Launch and Succeed steps.</p>
      <div className="flex flex-wrap gap-8">
        <fieldset className="m-0 border-0 p-0">
          <legend className="mb-2 p-0 text-sm font-semibold">Health</legend>
          <div className="flex flex-wrap gap-4">
            {(['on_track', 'needs_attention'] as const).map((h) => (
              <label key={h} className="flex min-h-11 items-center gap-2">
                <input type="radio" name="health" className="accent-brand h-[18px] w-[18px]" checked={r.health.state === h} onChange={() => void patch({ health: h }, 'Health updated.')} />
                {h === 'on_track' ? 'On track' : 'Needs attention'}
              </label>
            ))}
          </div>
          <div className="text-[13px] text-muted">Updated {fmtDate(r.health.updatedAt)}</div>
        </fieldset>
        <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); void patch({ freezeDate: freeze || null }, freeze ? 'Freeze date saved.' : 'Freeze date cleared.'); }}>
          <TextField label="Freeze date" type="date" value={freeze} onChange={(e) => setFreeze(e.target.value)} className="w-[200px]" />
          <Button type="submit" variant="outline">Save</Button>
        </form>
      </div>
      <div className="flex flex-col gap-4">
        {STAGES.map((st) => (
          <fieldset key={st.id} className="m-0 border-0 p-0">
            <legend className="section-label mb-1 p-0">{st.title}</legend>
            <ul className="m-0 flex list-none flex-col p-0">
              {r.statusSteps.filter((s) => s.stage === st.id).map((s) => (
                <li key={s.id} className="border-b border-line-faint py-1.5 last:border-b-0">
                  <label className={cx('flex min-h-9 items-start gap-2.5', s.source === 'auto' && 'text-muted')}>
                    <input
                      type="checkbox"
                      className="mt-0.5 h-[18px] w-[18px] flex-none"
                      checked={s.state === 'done'}
                      disabled={s.source === 'auto'}
                      onChange={(e) => setStep(s.id, e.target.checked)}
                    />
                    <span>
                      {s.label}
                      {s.source === 'auto' && <span className="text-[13px]"> · automatic</span>}
                      {s.at && s.state === 'done' && <span className="block text-[13px] text-muted">{fmtDate(s.at)}</span>}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        ))}
      </div>
    </div>
  );
}

type Draft = { summary: string; decidedAt: string; approvedBy: string };

export function DecisionsTab({ r, reload }: { r: Review; reload: () => Promise<void> }) {
  const empty = (): Draft => ({ summary: '', decidedAt: today(), approvedBy: r.header.contact?.name ?? '' });
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<Draft>(empty);
  const [err, setErr] = useState<string | null>(null);
  const base = `/api/admin/onboardings/${r.id}/decisions`;

  const save = async () => {
    setErr(null);
    try {
      if (editing === 'new') await api.post<DecisionView>(base, draft);
      else if (editing) await api.patch<DecisionView>(`${base}/${editing}`, draft);
      setEditing(null);
      await reload();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not save the decision.'); }
  };
  const remove = async (id: string) => {
    if (!window.confirm('Delete this decision? The firm will no longer see it.')) return;
    try { await api.del(`${base}/${id}`); await reload(); } catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not delete.'); }
  };

  const form = (
    <form className="flex flex-col gap-3 rounded-card border border-line bg-white p-[18px]" onSubmit={(e) => { e.preventDefault(); void save(); }}>
      <TextArea label="Decision" rows={2} value={draft.summary} onChange={(e) => setDraft({ ...draft, summary: e.target.value })} />
      <div className="flex flex-wrap gap-3">
        <TextField label="Decided on" type="date" value={draft.decidedAt} onChange={(e) => setDraft({ ...draft, decidedAt: e.target.value })} className="w-[200px]" />
        <TextField label="Approved by" value={draft.approvedBy} onChange={(e) => setDraft({ ...draft, approvedBy: e.target.value })} className="min-w-[200px] flex-1" />
      </div>
      <div className="flex gap-2">
        <Button type="submit" variant="primary" size="sm" disabled={!draft.summary.trim() || !draft.approvedBy.trim() || !draft.decidedAt}>Save decision</Button>
        <Button size="sm" onClick={() => setEditing(null)}>Cancel</Button>
      </div>
    </form>
  );

  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 text-muted">Customer-visible. Each decision appears on the firm’s status page under “Decisions we’ve made together”.</p>
      {err && <Banner tone="error" role="alert">{err}</Banner>}
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {r.decisions.map((d) => (
          <li key={d.id}>
            {editing === d.id ? form : (
              <div className="flex flex-wrap items-start justify-between gap-2 rounded-card border border-line px-[18px] py-3">
                <div className="min-w-0">
                  <div>{d.summary}</div>
                  <div className="text-[13px] text-muted">{fmtCalendarDate(d.decidedAt)} · Approved by {d.approvedBy}</div>
                </div>
                <div className="flex gap-1">
                  <Button variant="link" size="sm" onClick={() => { setDraft({ summary: d.summary, decidedAt: d.decidedAt.slice(0, 10), approvedBy: d.approvedBy }); setEditing(d.id); }}>Edit</Button>
                  <Button variant="quiet" size="sm" onClick={() => void remove(d.id)}>Delete</Button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
      {editing === 'new' ? form : <div><Button variant="outline" onClick={() => { setDraft(empty()); setEditing('new'); }}>Add a decision</Button></div>}
    </div>
  );
}

export function MessagesTab({ r, reload }: { r: Review; reload: () => Promise<void> }) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const send = async () => {
    setBusy(true);
    setErr(null);
    try { await api.post<MessageView>(`/api/admin/onboardings/${r.id}/messages`, { body: body.trim() }); setBody(''); await reload(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not send.'); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-3">
      {r.messages.length === 0 && <p className="m-0 text-muted">No messages yet.</p>}
      <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
        {r.messages.map((m) => (
          <li key={m.id} className={cx('max-w-[720px] rounded-xl px-3.5 py-2.5', m.from === 'staff' ? 'self-end bg-brand-tint' : m.unread ? 'border border-warn-line bg-warn-tint' : 'bg-subtle')}>
            <div className="flex flex-wrap items-center gap-x-2 text-[13px] font-semibold">
              <span>{m.authorName} <span className="font-normal text-muted">· {m.from === 'firm' ? 'Firm' : 'Staff'} · {fmtDateTime(m.at)}</span></span>
              {m.unread && <Pill tone="warn" className="!py-0 !text-xs">New</Pill>}
            </div>
            <div className="whitespace-pre-wrap [overflow-wrap:anywhere]">{m.body}</div>
          </li>
        ))}
      </ul>
      <TextArea label="Message the firm" hint="Appears on the firm’s status page and is emailed to the contact." rows={3} value={body} onChange={(e) => setBody(e.target.value)} />
      {err && <Banner tone="error" role="alert">{err}</Banner>}
      <div><Button variant="primary" size="sm" disabled={busy || !body.trim()} onClick={() => void send()}>Send message</Button></div>
    </div>
  );
}

export function ActivityTab({ r }: { r: Review }) {
  if (!r.events.length) return <p className="m-0 text-muted">No activity yet.</p>;
  return (
    <TableBox minWidth={560} label="Activity">
      <thead><tr className="border-b border-line-soft"><Th>When</Th><Th>Who</Th><Th>What</Th></tr></thead>
      <tbody>
        {r.events.map((e, i) => (
          <tr key={`${e.at}-${i}`} className="border-b border-line-soft last:border-b-0">
            <Td className="whitespace-nowrap text-muted">{fmtDateTime(e.at)}</Td>
            <Td>{e.actor}</Td>
            <Td>{e.summary}</Td>
          </tr>
        ))}
      </tbody>
    </TableBox>
  );
}
